import React from "react";
import {
  Modal,
  Typography,
  IconButton,
  Paper,
  CircularProgress,
  Box,
} from "@mui/material";
import { EzrImage, MaterialIcon } from "ui-widgets";
import Asset from "datalign-module-template/models/asset";
import CSVViewer from "./csv-viewer";
import {
  AUDIO_EXTENSIONS,
  CODE_EXTENSIONS,
  IMAGE_EXTENSIONS,
  TEXT_EXTENSIONS,
  VIDEO_EXTENSIONS,
} from "datalign-module-template/components/extensionsList";
import EzrVideo from "datalign-module-template/components/ezr-video";
import EzrAudio from "datalign-module-template/components/ezr-audio/EzrAudio";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import PDFViewer from "./pdf-viewer/PDFViewer";
import xmlFormatter from "xml-formatter";
import CodeViewer from "./code-viewer";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import DownloadAsset from "datalign-module-template/helpers/DownloadAsset";

const AssetPreviewModal = ({
  open,
  onClose,
  asset,
  fileContent,
  loaded,
  extension,
}: {
  open: boolean;
  onClose: () => void;
  asset: Asset | null;
  fileContent: string | ArrayBuffer | object | null;
  loaded: boolean;
  extension: string | null;
}) => {
  if (!asset) return null;

  const renderContent = () => {
    if (!extension) return <CircularProgress />;

    switch (extension) {
      case ".xml":
        if (typeof fileContent === "string")
          return <CodeViewer code={xmlFormatter(fileContent)} language="xml" />;
        break;

      case ".json":
      case ".geojson":
        return (
          <CodeViewer
            code={JSON.stringify(fileContent, null, 2)}
            language="json"
          />
        );

      case ".js":
      case ".ts":
      case ".tsx":
        if (typeof fileContent === "string")
          return <CodeViewer code={fileContent} language="typescript" />;
        break;

      case ".html":
      case ".htm":
        if (typeof fileContent === "string")
          return <CodeViewer code={fileContent} language="html" />;
        break;

      case ".css":
        if (typeof fileContent === "string")
          return <CodeViewer code={fileContent} language="css" />;
        break;

      case ".sh":
      case ".bash":
        if (typeof fileContent === "string")
          return <CodeViewer code={fileContent} language="bash" />;
        break;

      case ".pdf":
        if (fileContent instanceof ArrayBuffer)
          return <PDFViewer file={{ data: fileContent }} />;
        break;

      case ".csv":
        if (typeof fileContent === "string")
          return <CSVViewer csvContent={fileContent} />;
        break;

      default:
        if (
          (TEXT_EXTENSIONS.includes(extension) ||
            CODE_EXTENSIONS.includes(extension)) &&
          typeof fileContent === "string"
        ) {
          return <pre style={{ textAlign: "left" }}>{fileContent}</pre>;
        } else if (IMAGE_EXTENSIONS.includes(extension)) {
          return (
            <Zoom>
              <EzrImage
                key={`preview-image-${asset.guid}`}
                src={asset.presigned_url}
                alt={asset.name}
                loading="lazy"
                style={{
                  padding: 4,
                  maxWidth: "100%",
                  aspectRatio: "auto",
                  objectPosition: "center",
                  objectFit: "contain",
                }}
              />
            </Zoom>
          );
        } else if (VIDEO_EXTENSIONS.includes(extension)) {
          return (
            <EzrVideo
              key={`preview-video-${asset.guid}`}
              src={asset.presigned_url}
              controls
              style={{ maxWidth: "100%", padding: 4 }}
            />
          );
        } else if (AUDIO_EXTENSIONS.includes(extension)) {
          return (
            <EzrAudio
              key={`preview-audio-${asset.guid}`}
              src={asset.presigned_url}
              style={{ padding: 4 }}
              controls
            />
          );
        }
        return <Typography>This file format isn’t supported!</Typography>;
    }

    return <Typography>Something went wrong!</Typography>;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80%",
          height: "80%",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          overflow: "auto",
        }}
      >
        <Box>
          <Typography variant="h5">{asset.name}</Typography>
          <IconButton
            onClick={() => DownloadAsset(asset)}
            sx={{ position: "absolute", top: 8, right: 56 }}
          >
            <MaterialIcon icon="download" />
          </IconButton>
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <MaterialIcon icon="close" />
          </IconButton>
        </Box>

        <Box
          sx={{
            mx: -2,
            mb: -4,
            textAlign: "center",
            minHeight: "100%",
          }}
        >
          {loaded ? renderContent() : <CircularProgress />}
        </Box>
      </Paper>
    </Modal>
  );
};

export default AssetPreviewModal;
